import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="min-h-screen bg-paper dark:bg-dark-bg transition-colors px-6 py-16">
      <div className="max-w-3xl mx-auto bg-white dark:bg-dark-surface border border-hairline dark:border-dark-border rounded-lg p-8 md:p-12">

        {/* Back nav */}
        <div className="mb-8">
          <Link to="/" className="text-ink hover:underline text-sm">
            ← Back to ZUPA
          </Link>
        </div>

        {/* Title */}
        <h1 className="font-display text-2xl sm:text-3xl text-ink dark:text-dark-text mb-2 leading-snug">
          Terms and Conditions
        </h1>
        <p className="text-sm text-steel-dark dark:text-dark-muted mb-1">
          <strong>Last Updated:</strong> April 12, 2026
        </p>
        <p className="text-sm text-steel-dark dark:text-dark-muted mb-10">
          <strong>Effective Date:</strong> April 12, 2026
        </p>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 1 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            1. Introduction
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            These Terms and Conditions ("Terms") govern your access to and use of ZUPA ("the Service"), operated by <strong>Summerhill Technologies</strong> ("we," "us," "our"), a company incorporated in India.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            By creating an account or using ZUPA, you agree to be bound by these Terms and our{" "}
            <Link to="/privacy" className="text-ink hover:underline">Privacy Policy</Link>.
            If you do not agree to these Terms, do not use the Service.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            ZUPA is available at{" "}
            <a href="https://zupapost.ai" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">zupapost.ai</a>.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 2 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            2. Definitions
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"Service"</strong> means the ZUPA platform, including all features for AI-powered social media content generation, image generation, content scheduling, and social media posting.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"User," "you," "your"</strong> means any individual or entity that creates an account and uses the Service.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"Content"</strong> means text, images, graphics, and other materials.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"User Content"</strong> means any Content you provide to the Service, including prompts, brand information, company data, writing samples, logos, and images you upload.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"Generated Content"</strong> means any Content produced by the Service using artificial intelligence, including social media posts, images, captions, and suggestions.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"Workspace"</strong> means a dedicated profile within your account containing company information, brand settings, and service descriptions used for content generation.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>"Subscription"</strong> means your paid or trial access plan for the Service.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 3 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            3. Eligibility
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You must be at least 18 years of age to use ZUPA. By creating an account, you represent and warrant that you are at least 18 years old, that you have the legal capacity to enter into these Terms, and that all information you provide during registration is accurate and complete.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            If you are using ZUPA on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 4 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            4. Account Registration and Security
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You must provide a valid email address and create a password (or sign in via Google OAuth) to create an account. You are responsible for maintaining the confidentiality of your login credentials, all activity that occurs under your account, and notifying us immediately at{" "}
            <a href="mailto:info@coderootz.com" className="text-ink hover:underline">info@coderootz.com</a>{" "}
            if you suspect unauthorized access.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We reserve the right to suspend or terminate accounts that provide false information, engage in fraudulent activity (including trial abuse through multiple accounts or device manipulation), or violate these Terms.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 5 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            5. The Service
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            5.1 What ZUPA Does
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            ZUPA is an AI-powered platform that generates social media content optimized using behavioral psychology principles. The Service uses Google's Gemini AI to create text posts, image suggestions, and AI-generated images for platforms including LinkedIn, Instagram, Twitter/X, and Facebook.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            5.2 AI-Generated Content Disclosure
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>All content produced by ZUPA is generated by artificial intelligence.</strong> Specifically:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-steel-dark dark:text-dark-muted">
            <li>Text content is generated by Google's Gemini AI, guided by behavioral psychology principles from researchers such as Robert Cialdini and Daniel Kahneman.</li>
            <li>AI-generated images are produced using Google's image generation capabilities.</li>
            <li>The AI selects and applies psychology principles dynamically based on your content and context. You can view which principles were applied to each generated post.</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            Under the EU AI Act and applicable transparency regulations, we are required to inform you that you are interacting with an AI system when using ZUPA's content generation features.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            5.3 How Your Data Is Used for Generation
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            When you generate content, the following information from your workspace is sent to Google's Gemini AI API: your prompt, company name, description, industry, target audience, brand voice, writing samples, founder story, key phrases, service descriptions, and audience persona profiles. For full details, see our{" "}
            <Link to="/privacy" className="text-ink hover:underline">Privacy Policy</Link>.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            5.4 Content Review Responsibility
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>You are solely responsible for reviewing, editing, and approving all Generated Content before publishing or using it in any way.</strong> ZUPA provides content suggestions as a starting point. We do not guarantee that Generated Content is accurate, appropriate, free from bias, factually correct, or compliant with any platform's terms of service or community guidelines.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            5.5 No Professional Advice
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            The psychological principles applied to content generation are for marketing optimization purposes only. They do not constitute professional advice from a psychologist, marketing consultant, business advisor, or any other professional. You should not rely on Generated Content as a substitute for professional guidance.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 6 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            6. Subscriptions and Payments
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            6.1 Plans
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            ZUPA offers the following subscription tiers:
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm text-steel-dark dark:text-dark-muted border border-hairline dark:border-dark-border">
              <thead>
                <tr className="bg-paper dark:bg-dark-surface">
                  <th className="border border-hairline dark:border-dark-border px-4 py-2 text-left font-semibold text-ink dark:text-dark-text">Feature</th>
                  <th className="border border-hairline dark:border-dark-border px-4 py-2 text-left font-semibold text-ink dark:text-dark-text">Basic</th>
                  <th className="border border-hairline dark:border-dark-border px-4 py-2 text-left font-semibold text-ink dark:text-dark-text">Basic+</th>
                  <th className="border border-hairline dark:border-dark-border px-4 py-2 text-left font-semibold text-ink dark:text-dark-text">Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Price (USD)</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">$10/month</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">$25/month</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">$50/month</td>
                </tr>
                <tr className="bg-paper dark:bg-dark-surface">
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Platforms per generation</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">1</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">2</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">4</td>
                </tr>
                <tr>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Generations per month</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">20</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">50</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">200</td>
                </tr>
                <tr className="bg-paper dark:bg-dark-surface">
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Workspaces</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">2</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">5</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">8</td>
                </tr>
                <tr>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Saved prompts</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">10</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">30</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">50</td>
                </tr>
                <tr className="bg-paper dark:bg-dark-surface">
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">AI images per month</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">0</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">15</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">30</td>
                </tr>
                <tr>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Service-specific posts</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">No</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Yes</td>
                  <td className="border border-hairline dark:border-dark-border px-4 py-2">Yes</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            Pricing is also available in INR, EUR, and AED. Current pricing is displayed on our pricing page.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            6.2 Free Trial
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            New users receive a 15-day free trial with access to Pro-tier features. No payment information is required to start a trial. At the end of the trial, you must subscribe to a paid plan to continue using the Service.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            6.3 Billing and Renewal
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            Subscriptions are billed in advance on a monthly or annual basis through Razorpay. <strong>Subscriptions automatically renew</strong> at the end of each billing period unless you cancel before the renewal date. You will be charged at the then-current rate for your plan.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            6.4 Cancellation
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You may cancel your subscription at any time from your account settings. Upon cancellation, you will continue to have access to the Service until the end of your current billing period. No refunds are provided for partial billing periods.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>For users in the European Union:</strong> You have the right to cancel your subscription within 14 days of purchase without giving any reason (cooling-off period), provided you have not used the Service substantially during that period. To exercise this right, contact us at{" "}
            <a href="mailto:info@coderootz.com" className="text-ink hover:underline">info@coderootz.com</a>.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            6.5 Refund Policy
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We generally do not offer refunds for subscription fees already paid. Exceptions may be made in cases of billing errors, service unavailability for extended periods caused by us, or where required by applicable consumer protection law.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            6.6 Price Changes
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We may change subscription prices with at least 30 days' notice. Price changes will apply to the next billing cycle after the notice period. If you do not agree with a price change, you may cancel your subscription before the new price takes effect.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 7 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            7. User Content and Intellectual Property
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            7.1 Your Content
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You retain all ownership rights to User Content you provide to ZUPA, including your brand information, prompts, writing samples, logos, and uploaded images. By using the Service, you grant us a limited, non-exclusive, worldwide license to use your User Content solely for the purpose of providing and improving the Service, including sending it to our AI provider for content generation. This license terminates when you delete your content or your account.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            7.2 Generated Content
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            Generated Content is created by AI based on your inputs. You are granted a non-exclusive, perpetual license to use, modify, publish, and distribute Generated Content for any lawful purpose. We do not claim ownership of Generated Content.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3 leading-relaxed">
            <strong>Important limitations:</strong>
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-steel-dark dark:text-dark-muted">
            <li>Generated Content is not guaranteed to be unique. Similar or identical content may be generated for other users who provide similar inputs.</li>
            <li>We do not guarantee that Generated Content does not infringe third-party intellectual property rights. You are responsible for ensuring that your use of Generated Content does not violate any laws or third-party rights.</li>
            <li>AI-generated images may not be unique, and copyright protection for AI-generated works varies by jurisdiction.</li>
          </ul>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            7.3 Our Intellectual Property
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            The ZUPA platform, including its design, code, psychology principles database, algorithms, documentation, and branding, is owned by Summerhill Technologies and protected by intellectual property laws. These Terms do not grant you any rights to our intellectual property beyond what is necessary to use the Service.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            7.4 Feedback
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            If you provide feedback, suggestions, or ideas about the Service, we may use them without obligation to you.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 8 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            8. Social Media Connections
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            8.1 Connecting Accounts
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You may connect your social media accounts (LinkedIn, Instagram, Twitter/X, Facebook) to ZUPA to enable direct posting. When you connect an account, you authorize ZUPA to post content that you explicitly approve to your connected accounts, access basic profile information displayed within the ZUPA interface, and store encrypted authentication tokens for the connection.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            8.2 Your Responsibility
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You are responsible for ensuring you have the authority to connect and post to any social media account (including pages or business accounts you manage), that content you publish through ZUPA complies with the respective platform's terms of service and community guidelines, and that you review all content before authorizing it to be posted.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            8.3 Disconnecting Accounts
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You may disconnect any social media account at any time from your Settings. When you disconnect, we revoke the authentication token on the platform side and remove it from our systems.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            8.4 Agency and Multi-Workspace Use
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            If you manage workspaces and social media accounts on behalf of clients (such as agencies or freelancers managing multiple brands), you represent and warrant that you have proper authorization from each client to use their brand data within ZUPA, connect and post to their social media accounts, and process their business information through the Service (including sending it to our AI provider).
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You are responsible for ensuring compliance with data protection laws regarding any third-party data you input into ZUPA.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 9 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            9. Acceptable Use
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You agree not to use ZUPA to generate, publish, or distribute content that is illegal, harmful, threatening, harassing, defamatory, or discriminatory; infringes on any third party's intellectual property, privacy, or other rights; is deliberately misleading, deceptive, or fraudulent; constitutes spam or unsolicited commercial messages; promotes violence, self-harm, or illegal activities; contains sexually explicit material; or impersonates any person or entity.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You also agree not to attempt to access other users' accounts or data; reverse-engineer, decompile, or disassemble any part of the Service; use automated tools (bots, scrapers) to access the Service except through our official API; circumvent any security measures, rate limits, or usage restrictions; create multiple accounts to abuse free trials or promotional offers; or use the Service in any way that could harm, disable, or impair the Service.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We reserve the right to suspend or terminate your account and remove any content that violates these Terms, without prior notice and at our sole discretion.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 10 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            10. Limitation of Liability
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>To the maximum extent permitted by applicable law:</strong>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We do not warrant that the Service will be uninterrupted, error-free, or secure; that Generated Content will be accurate, appropriate, unique, or free from bias; that Generated Content will comply with any specific platform's requirements; that applying psychological principles will achieve any specific marketing result; or that AI-generated images will be suitable for any particular purpose.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3 leading-relaxed">
            <strong>We shall not be liable for:</strong>
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-steel-dark dark:text-dark-muted">
            <li>Any indirect, incidental, special, consequential, or punitive damages.</li>
            <li>Loss of profits, revenue, data, or business opportunities.</li>
            <li>Damages arising from your use of Generated Content, including any claims by third parties.</li>
            <li>Damages arising from unauthorized access to your social media accounts, except where caused by our gross negligence.</li>
            <li>Actions taken by social media platforms in response to content posted through ZUPA.</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>Our total aggregate liability</strong> for any claims arising from your use of the Service shall not exceed the total subscription fees you have paid to us in the twelve (12) months preceding the claim.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>For users in the European Union:</strong> Nothing in these Terms limits or excludes our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any liability that cannot be excluded or limited under applicable EU consumer protection law.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 11 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            11. Indemnification
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You agree to indemnify, defend, and hold harmless Summerhill Technologies, its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising from your use of the Service or Generated Content; your violation of these Terms; your violation of any third-party rights; content you publish through the Service; your connection and use of third-party social media accounts through ZUPA; and User Content you provide that infringes any third party's rights.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 12 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            12. Termination
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            12.1 By You
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            You may cancel your subscription and delete your account at any time from your account settings. Upon account deletion, all your personal data will be permanently removed as described in our Privacy Policy.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            12.2 By Us
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We may suspend or terminate your account immediately if you violate these Terms, engage in fraudulent activity, use the Service in a manner that could harm us or other users, or fail to pay subscription fees when due. We will make reasonable efforts to notify you before or at the time of termination, except where immediate action is necessary.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mt-6 mb-3">
            12.3 Effect of Termination
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            Upon termination, your right to use the Service ceases immediately. We will delete your data in accordance with our Privacy Policy. You may export your data before termination using the data export feature. Any provisions of these Terms that by their nature should survive termination will survive, including Sections 7 (Intellectual Property), 10 (Limitation of Liability), 11 (Indemnification), and 13 (Governing Law).
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 13 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            13. Governing Law and Disputes
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            These Terms are governed by and construed in accordance with the laws of India, without regard to conflict of law principles. Any disputes arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the courts located in Shimla, Himachal Pradesh, India.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>For users in the European Union:</strong> These Terms do not affect your statutory rights under mandatory EU consumer protection law, including the GDPR. You retain the right to bring proceedings in the courts of your country of residence. You may also use the European Commission's Online Dispute Resolution platform at{" "}
            <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">ec.europa.eu/odr</a>.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>For users in the UAE:</strong> Nothing in these Terms limits your rights under UAE Federal consumer protection law.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>For users in Saudi Arabia:</strong> Nothing in these Terms limits your rights under the laws of the Kingdom of Saudi Arabia.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 14 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            14. Service Availability and Modifications
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We strive to keep ZUPA available and operational, but we do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or technical issues. We may also experience disruptions due to third-party services we depend on (Google Gemini API, social media platform APIs, Razorpay).
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We reserve the right to modify, suspend, or discontinue any part of the Service at any time. For material changes that significantly affect your use, we will provide at least 30 days' notice. Feature changes within your subscription tier will be communicated via in-app notices or email.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 15 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            15. Changes to These Terms
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            We may update these Terms from time to time. We will notify you of material changes by displaying a notice within the Service and, for significant changes, by sending an email to your registered email address. We will provide at least 30 days' notice for material changes.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms. If you do not agree with the changes, you may cancel your subscription and delete your account before the changes take effect.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 16 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            16. Miscellaneous
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and Summerhill Technologies regarding the Service.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>No Waiver:</strong> Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>Assignment:</strong> You may not assign or transfer your rights under these Terms without our written consent. We may assign our rights and obligations without restriction.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>Force Majeure:</strong> We are not liable for delays or failures resulting from circumstances beyond our reasonable control, including natural disasters, government actions, internet outages, or third-party service failures.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        {/* Section 17 */}
        <section>
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mt-10 mb-4">
            17. Contact Us
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            For questions about these Terms:
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-2 leading-relaxed">
            <strong>Email:</strong>{" "}
            <a href="mailto:info@coderootz.com" className="text-ink hover:underline">info@coderootz.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-2 leading-relaxed">
            <strong>Business Contact:</strong>{" "}
            <a href="mailto:bd@summerhilltechnologies.com" className="text-ink hover:underline">bd@summerhilltechnologies.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4 leading-relaxed">
            <strong>Website:</strong>{" "}
            <a href="https://zupapost.ai" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">zupapost.ai</a>
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border my-8" />

        <p className="text-sm text-steel-dark dark:text-dark-muted italic">
          These Terms and Conditions are provided for informational purposes. We recommend that users with specific legal concerns consult with a qualified legal professional.
        </p>

      </div>
    </div>
  );
}
